import AddAffiliateButton from '@/components/affiliate/AddAffiliateButton';
import AffiliateBulkActionBar from '@/components/affiliate/AffiliateBulkActionBar';
import AffiliateHiddenDebtNotice from './AffiliateHiddenDebtNotice';
import AffiliateProgramActions from './AffiliateProgramActions';
import AffiliateSelectAllBanner from '@/components/affiliate/table/AffiliateSelectAllBanner';
import BulkAffiliateEmailModal from '@/components/affiliate/bulk-email/BulkAffiliateEmailModal';
import AffiliateExportModal from '@/components/affiliate/export/AffiliateExport.Modal';
import type { AffiliateExportBaseFilters } from '@/components/affiliate/export/AffiliateExport.Modal';
import AffiliateImportModal from '@/pages/affiliate-import/AffiliateImport.Modal';
import PaginationControls from '@/components/shared/PaginationControls';
import TableSkeleton from '@/components/table/TableSkeleton';
import DateCell from '@/components/table/DateCell';
import { CreatorUsernameLink } from '@/components/creators/CreatorAvatar';
import { DashboardTableActiveFilters } from '@/components/table/DashboardTableActiveFilters';
import { SortableColumnHeader } from '@/components/table/SortableColumnHeader';
import {
  TableScrollArea,
  stickyColumnProps,
  stickyEndColumnProps,
} from '@/components/table/TableScrollArea';
import type { DashboardTableFilterItem } from '@/components/table/dashboard-table-utils';
import { formatDashboardTableDateRange } from '@/components/table/dashboard-table-utils';
import { useDashboardTableQueryState } from '@/components/table/useDashboardTableQueryState';
import Money from '@/components/text/money';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterPanel } from '@/components/ui/filter-panel';
import { toaster } from '@/components/ui/toaster';
import { useSearchAffiliatesQuery } from '@/hooks/queries/use-search-affiliates-query';
import useAffiliateBulkEmailStore from '@/stores/affiliate-bulk-email-store';
import { Badge, FormatNumber, HStack, IconButton, Table, Text, VStack } from '@chakra-ui/react';
import { Checkbox } from '@/components/ui/checkbox';
import type {
  AffiliatePayoutStatus,
  AffiliateSourceCreatorAttribution,
  AffiliateTableDateFilterType,
  AffiliateTableSortBy,
  AffiliateUserListItem,
  BulkAffiliateEmailFilters,
  DashboardTableSortState,
  ListAffiliateUsersResponse,
} from '@fluenceur/types';

import { parseAsBoolean, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import AffiliateInfo from '../AffiliateInfo';
import AffiliatesFilter from './filters/Affiliates.Filter';
import {
  Copy,
  Eye,
  Instagram,
  Mail,
  Network,
  Puzzle,
  Search,
  Tiktok,
  UploadDataWindow,
  User,
  WarningTriangle,
} from 'iconoir-react';
import { Tooltip } from '@/components/ui/tooltip';
import { getPlatformProfileUrl } from '@/components/creator-discovery/utils/creator-formatters';
import { useOpenAffiliateSurface } from '@/hooks/use-open-affiliate-surface';
import { writeClipboardText } from '@/libs/clipboard';
import { AffiliateProgramStatus } from '@fluenceur/types';

interface AffiliatesTableProps {
  programId?: string;
}

const EMPTY_AFFILIATES: AffiliateUserListItem[] = [];
const SELECTION_COLUMN_WIDTH = 'calc(var(--table-bleed-x) + 2rem)';
const AFFILIATE_COLUMN_WIDTH = '300px';
const NETWORKS_COLUMN_WIDTH = '190px';
const ACTIONS_COLUMN_WIDTH = '150px';
// Standalone empty state, so the glyph is sized like the one on Invitations
// rather than like an in-cell icon.
const EMPTY_ICON_SIZE = 48;
const AFFILIATES_TABLE_STYLE = {
  '--table-selection-padding-start': 'var(--table-bleed-x)',
  '--table-min-width': '1930px',
} as CSSProperties;
const AFFILIATE_TABLE_SORT_OPTIONS = [
  'joinedAt',
  'orders',
  'visits',
  'revenue',
  'conversion',
  'averageOrderValue',
  'payableCommission',
  'lastOrderAt',
] as const satisfies readonly [AffiliateTableSortBy, ...AffiliateTableSortBy[]];
const AFFILIATE_TABLE_DATE_FILTER_TYPES = ['joinedAt', 'lastOrderAt'] as const satisfies readonly [
  AffiliateTableDateFilterType,
  ...AffiliateTableDateFilterType[],
];
const AFFILIATE_DEFAULT_SORT = {
  sortBy: 'joinedAt',
  sortDirection: 'desc',
} as const satisfies DashboardTableSortState<AffiliateTableSortBy>;
type AffiliateProgramListItem = AffiliateUserListItem['programs'][number];

const pickCreatorHandle = (creator?: AffiliateSourceCreatorAttribution | null): string | null => {
  const handle = creator?.username?.trim();

  if (!handle) return null;

  return handle.startsWith('@') ? handle.slice(1) : handle;
};

const getPlatformKey = (platform?: string | null) => {
  const normalizedPlatform = platform?.trim().toLowerCase();

  if (normalizedPlatform === 'instagram' || normalizedPlatform === 'tiktok') {
    return normalizedPlatform;
  }

  return normalizedPlatform || null;
};

const getProgramOriginKey = (program?: AffiliateProgramListItem | null) => {
  const platformKey = getPlatformKey(program?.invitedFromCreator?.platform);

  if (program?.source === 'discovery' && platformKey) return `discovery_${platformKey}`;
  if (program?.source) return program.source;

  return platformKey;
};

type SocialProfile = {
  platform: string | null;
  username: string;
  url?: string;
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
};

const PAYOUT_STATUS_COLOR: Record<AffiliatePayoutStatus, 'gray' | 'green' | 'orange' | 'blue'> = {
  none: 'gray',
  payable: 'orange',
  processing: 'blue',
  paid: 'green',
};

const AffiliatesTable: React.FC<AffiliatesTableProps> = ({ programId }) => {
  const { t } = useTranslation();
  // Read URL state for filters - using namespaced parameters
  const [search, setSearch] = useQueryState('affiliateSearch', parseAsString.withDefault(''));
  const [status, setStatus] = useQueryState('affiliateStatus', parseAsString.withDefault('all'));
  const tableState = useDashboardTableQueryState<
    AffiliateTableSortBy,
    AffiliateTableDateFilterType
  >({
    namespace: 'affiliate',
    sortByOptions: AFFILIATE_TABLE_SORT_OPTIONS,
    defaultSort: AFFILIATE_DEFAULT_SORT,
    dateFilterTypeOptions: AFFILIATE_TABLE_DATE_FILTER_TYPES,
    defaultDateFilterType: 'joinedAt',
  });
  const {
    query: tableQuery,
    sort: tableSort,
    dateFilter,
    setPage: setTablePage,
    setSort: setTableSort,
    setDateFilter,
    clearDateFilter,
  } = tableState;
  // CSV import wizard's Done step deep-links here with `?import_batch_id=:batchId`.
  // Backend resolves the JOIN through bulk_invitation_items + program_invitations.
  const [importBatchId, setImportBatchId] = useQueryState(
    'import_batch_id',
    parseAsString.withDefault('')
  );
  // Comma-separated AffiliateEnrollmentSource values; backend filters via the same JOIN.
  const [sourceFilter, setSourceFilter] = useQueryState(
    'affiliateSource',
    parseAsString.withDefault('')
  );

  // Opens the affiliate surface (unified contact drawer)
  const openAffiliateSurface = useOpenAffiliateSurface();

  const selectedIds = useAffiliateBulkEmailStore((s) => s.selectedIds);
  const selectAllMatching = useAffiliateBulkEmailStore((s) => s.selectAllMatching);
  const toggleSelection = useAffiliateBulkEmailStore((s) => s.toggleSelection);
  const toggleSelectAll = useAffiliateBulkEmailStore((s) => s.toggleSelectAll);
  const clearSelection = useAffiliateBulkEmailStore((s) => s.clearSelection);
  const setMatchingTotal = useAffiliateBulkEmailStore((s) => s.setMatchingTotal);

  // Modal open state via URL
  const [isBulkEmailOpen, setIsBulkEmailOpen] = useQueryState(
    'bulkEmail',
    parseAsBoolean.withDefault(false)
  );
  const [, setIsAffiliateExportOpen] = useQueryState(
    'affiliateExport',
    parseAsBoolean.withDefault(false)
  );

  // Clear the selection whenever the filtered cohort changes (program or any
  // filter dimension) so "select all matching" never targets a stale set.
  // Pagination is intentionally excluded: the selection persists across pages.
  useEffect(() => {
    clearSelection();
  }, [
    programId,
    search,
    status,
    sourceFilter,
    importBatchId,
    tableQuery.dateFilterType,
    tableQuery.startDate,
    tableQuery.endDate,
    clearSelection,
  ]);

  // Reset page to 1 when filters change (skip initial mount to respect URL state)
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (hasMountedRef.current) {
      setTablePage(1);
    } else {
      hasMountedRef.current = true;
    }
  }, [search, status, sourceFilter, importBatchId, setTablePage]);

  const handleSearchChange = useCallback(
    (nextSearch: string) => {
      void setSearch(nextSearch);
    },
    [setSearch]
  );

  const handleStatusChange = useCallback(
    (nextStatus: 'active' | 'inactive' | 'all' | undefined) => {
      void setStatus(nextStatus || 'all');
    },
    [setStatus]
  );

  const handleOpenExport = useCallback(() => {
    void setIsAffiliateExportOpen(true);
  }, [setIsAffiliateExportOpen]);

  // Build filters for the query
  const filters = {
    search: search || undefined,
    status: (status as 'active' | 'inactive' | 'all') || 'all',
    sortBy: tableQuery.sortBy,
    sortDirection: tableQuery.sortDirection,
    dateFilterType: tableQuery.dateFilterType,
    startDate: tableQuery.startDate,
    endDate: tableQuery.endDate,
    page: tableQuery.page,
    perPage: tableQuery.perPage,
    programId,
    source: sourceFilter || undefined,
    importBatchId: importBatchId || undefined,
  };

  const exportFilters = useMemo<AffiliateExportBaseFilters>(
    () => ({
      search: search || undefined,
      status: (status as 'active' | 'inactive' | 'all') || 'all',
      sort_by: tableQuery.sortBy,
      sort_direction: tableQuery.sortDirection,
      date_filter_type: tableQuery.dateFilterType,
      start_date: tableQuery.startDate,
      end_date: tableQuery.endDate,
      source: sourceFilter || undefined,
      import_batch_id: importBatchId || undefined,
    }),
    [
      importBatchId,
      search,
      sourceFilter,
      status,
      tableQuery.dateFilterType,
      tableQuery.endDate,
      tableQuery.sortBy,
      tableQuery.sortDirection,
      tableQuery.startDate,
    ]
  );
  const selectedAffiliateUserIds = useMemo(() => Array.from(selectedIds), [selectedIds]);

  // Track if we've ever loaded data to avoid skeleton on filter changes
  const hasLoadedDataRef = useRef(false);
  const lastSettledDataRef = useRef<ListAffiliateUsersResponse | undefined>(undefined);

  // Use the search affiliates query directly
  const { data, isFetching, error } = useSearchAffiliatesQuery(filters);

  useEffect(() => {
    if (!isFetching && data) {
      lastSettledDataRef.current = data;
    }
  }, [data, isFetching]);

  const displayData = data ?? (isFetching ? lastSettledDataRef.current : undefined);
  const affiliates = displayData?.data ?? EMPTY_AFFILIATES;
  const paginationMeta = displayData?.meta;

  // Memoized recipient list for bulk email components
  const recipientList = useMemo(
    () =>
      affiliates.map((a) => ({
        id: a.id,
        name: a.name,
        avatarUrl: a.avatar?.fileUrl,
        email: a.email,
        programs: a.programs.map((p) => ({ id: p.id, name: p.name })),
      })),
    [affiliates]
  );

  const pageIds = useMemo(() => affiliates.map((a) => a.id), [affiliates]);
  const matchingTotal = paginationMeta?.total ?? 0;

  // Keep the store's matching total in sync with the filtered result so the
  // "select all matching" banner and bulk bar show the right count.
  useEffect(() => {
    setMatchingTotal(matchingTotal);
  }, [matchingTotal, setMatchingTotal]);

  const allPageSelected =
    selectAllMatching || (pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id)));
  const somePageSelected = !allPageSelected && pageIds.some((id) => selectedIds.has(id));

  // The export filters narrowed to the bulk-email contract (sort is irrelevant
  // to recipient resolution); the modal sends this in "select all matching" mode.
  const bulkEmailFilters = useMemo<BulkAffiliateEmailFilters>(
    () => ({
      search: exportFilters.search,
      status: exportFilters.status,
      source: exportFilters.source,
      import_batch_id: exportFilters.import_batch_id,
      date_filter_type: exportFilters.date_filter_type,
      start_date: exportFilters.start_date,
      end_date: exportFilters.end_date,
    }),
    [exportFilters]
  );

  // Mark as loaded once we get any data
  if (displayData && !hasLoadedDataRef.current) {
    hasLoadedDataRef.current = true;
  }

  // Only show skeleton if we're fetching AND have never loaded data before
  const isInitialLoading = isFetching && !hasLoadedDataRef.current;

  // Handle view action - open the contact drawer via URL params. In a
  // program-specific view, deep-link to this affiliate's program config (the
  // surface hook maps the intent to the drawer's affiliation view).
  const handleView = (userId: string) => {
    openAffiliateSurface(
      userId,
      programId ? { view: 'affiliation', collab: programId } : undefined
    );
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setTablePage(newPage);
  };

  const hasDateFilter = Boolean(dateFilter.startDate || dateFilter.endDate);
  const hasActiveFilters = Boolean(
    search || status !== 'all' || sourceFilter || importBatchId || hasDateFilter
  );

  // Rendered on its own, in place of the table rather than inside it: an empty
  // list reads as a single message instead of a grid of headers wrapped around
  // nothing. Same treatment as the Invitations list.
  const renderEmptyState = () => (
    <EmptyState
      icon={<User width={EMPTY_ICON_SIZE} height={EMPTY_ICON_SIZE} strokeWidth={1.8} />}
      title={t(
        hasActiveFilters ? 'affiliates.table.empty.filteredTitle' : 'affiliates.table.empty.title'
      )}
      description={t(
        hasActiveFilters
          ? 'affiliates.table.empty.filteredDescription'
          : 'affiliates.table.empty.description'
      )}
    />
  );

  const sortedColumnCellProps = (column: AffiliateTableSortBy) => ({
    bg: tableSort.sortBy === column ? 'gray.50/70' : undefined,
  });

  const renderAffiliateRow = (affiliate: AffiliateUserListItem) => {
    const isProgramSpecificView = !!programId;
    const hasMultiplePrograms = affiliate.programs.length > 1;
    const displayedProgram = isProgramSpecificView
      ? (affiliate.programs.find((program) => program.id === programId) ?? affiliate.programs[0])
      : (affiliate.programs.find((program) => program.invitedFromCreator) ?? affiliate.programs[0]);
    const isSingleProgramContext = isProgramSpecificView || !hasMultiplePrograms;
    const copyValue =
      isSingleProgramContext && displayedProgram?.status !== AffiliateProgramStatus.REMOVED
        ? displayedProgram?.referralLink || displayedProgram?.referralCode || ''
        : '';
    const originPrograms = isProgramSpecificView ? [displayedProgram] : affiliate.programs;
    const distinctOriginKeys = new Set(
      originPrograms.map(getProgramOriginKey).filter((origin): origin is string => !!origin)
    );
    const hasMultipleOrigins =
      !isProgramSpecificView && hasMultiplePrograms && distinctOriginKeys.size > 1;
    const joinedAt = isSingleProgramContext
      ? (displayedProgram?.enrolledAt ?? affiliate.createdAt)
      : affiliate.createdAt;

    const handleCopyReferral = async () => {
      if (!copyValue) return;

      if (await writeClipboardText(copyValue)) {
        toaster.success({
          title: t('affiliates.table.actions.copySuccessTitle'),
          description: t('affiliates.table.actions.copySuccessDescription'),
          duration: 2000,
        });
      } else {
        toaster.error({
          title: t('affiliates.table.actions.copyErrorTitle'),
          description: t('affiliates.table.actions.copyErrorDescription'),
          duration: 3000,
        });
      }
    };

    const getNetworkProfiles = (): SocialProfile[] => {
      const programs = isProgramSpecificView ? [displayedProgram] : affiliate.programs;
      const profiles = new Map<string, SocialProfile>();

      programs.forEach((program) => {
        const handle = pickCreatorHandle(program?.invitedFromCreator);
        if (!handle) return;

        const platform = getPlatformKey(program?.invitedFromCreator?.platform);
        const key = `${platform ?? 'social'}:${handle.toLowerCase()}`;

        profiles.set(key, {
          platform,
          username: handle,
          url: getPlatformProfileUrl(program?.invitedFromCreator?.platform ?? undefined, handle),
        });
      });

      return Array.from(profiles.values());
    };

    const getOriginLabel = (program?: AffiliateProgramListItem | null) => {
      const platformKey = getPlatformKey(program?.invitedFromCreator?.platform);

      if ((program?.source === 'discovery' || !program?.source) && platformKey) {
        return t(`affiliates.table.sources.discovery_${platformKey}`, {
          defaultValue: t('affiliates.table.sources.discovery'),
        });
      }

      if (!program?.source) return t('dashboard.stats.notAvailable');

      return t(`affiliates.table.sources.${program.source}`);
    };

    const renderOriginIcon = (program?: AffiliateProgramListItem | null) => {
      const originKey = getProgramOriginKey(program);

      if (originKey === 'discovery_instagram') {
        return <Instagram width={14} height={14} aria-hidden="true" />;
      }

      if (originKey === 'discovery_tiktok') {
        return <Tiktok width={14} height={14} aria-hidden="true" />;
      }

      if (program?.source === 'invitation') {
        return <Mail width={14} height={14} aria-hidden="true" />;
      }

      if (program?.source === 'csv_import') {
        return <UploadDataWindow width={14} height={14} aria-hidden="true" />;
      }

      if (program?.source === 'extension') {
        return <Puzzle width={14} height={14} aria-hidden="true" />;
      }

      if (program?.source === 'manual') {
        return <User width={14} height={14} aria-hidden="true" />;
      }

      if (program?.source === 'discovery') {
        return <Search width={14} height={14} aria-hidden="true" />;
      }

      return <Network width={14} height={14} aria-hidden="true" />;
    };

    const renderOriginCell = () => {
      if (hasMultipleOrigins) {
        return (
          <Badge size="sm" variant="subtle" colorPalette="gray" gap={1.5}>
            <Network width={14} height={14} aria-hidden="true" />
            {t('affiliates.table.sources.multiple')}
          </Badge>
        );
      }

      const hasOrigin = Boolean(getProgramOriginKey(displayedProgram));

      if (!hasOrigin) {
        return (
          <Text fontSize="sm" color="fg.muted">
            {t('dashboard.stats.notAvailable')}
          </Text>
        );
      }

      return (
        <VStack align="flex-start" gap={0.5} minW={0}>
          <Badge size="sm" variant="subtle" colorPalette="gray" gap={1.5}>
            {renderOriginIcon(displayedProgram)}
            {getOriginLabel(displayedProgram)}
          </Badge>
        </VStack>
      );
    };

    const renderNetworksCell = () => {
      const profiles = getNetworkProfiles();

      if (profiles.length === 0) {
        return (
          <Text fontSize="sm" color="fg.subtle">
            {t('dashboard.stats.notAvailable')}
          </Text>
        );
      }

      return (
        <VStack align="flex-start" gap={2} minW={0}>
          {profiles.map((profile) => {
            const platformLabel = profile.platform
              ? t(`affiliates.table.sources.${profile.platform}`, {
                  defaultValue: profile.platform,
                })
              : t('affiliates.table.columns.networks');

            return (
              <VStack
                key={`${profile.platform ?? 'social'}:${profile.username}`}
                align="flex-start"
                gap={0}
                minW={0}
                maxW="full"
              >
                <Text
                  as="span"
                  color="fg.muted"
                  fontSize="xs"
                  fontWeight="medium"
                  lineHeight="1.25"
                  lineClamp={1}
                >
                  {platformLabel}
                </Text>
                <CreatorUsernameLink
                  username={profile.username}
                  profileUrl={profile.url}
                  maxW="full"
                  onClick={(event) => event.stopPropagation()}
                  textProps={{ color: 'primary.focusRing' }}
                />
              </VStack>
            );
          })}
        </VStack>
      );
    };

    // Get program name to display
    const getProgramNameDisplay = () => {
      if (isProgramSpecificView) {
        // In program-specific view, show the program name or hide it since it's redundant
        return displayedProgram?.name || '';
      }
      return hasMultiplePrograms
        ? t('affiliates.table.status.programs', { count: affiliate.programs.length })
        : displayedProgram?.name;
    };

    return (
      <Table.Row key={affiliate.id} _hover={{ bg: 'gray.50' }}>
        <Table.Cell
          width={SELECTION_COLUMN_WIDTH}
          {...stickyColumnProps(SELECTION_COLUMN_WIDTH, '0px', { divider: 'end' })}
        >
          <Checkbox
            size="sm"
            checked={selectAllMatching || selectedIds.has(affiliate.id)}
            disabled={selectAllMatching}
            onCheckedChange={() => toggleSelection(affiliate.id)}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          />
        </Table.Cell>
        <Table.Cell {...stickyColumnProps(AFFILIATE_COLUMN_WIDTH, SELECTION_COLUMN_WIDTH)}>
          <AffiliateInfo
            name={affiliate.name}
            avatarUrl={affiliate.avatar?.fileUrl}
            programName={getProgramNameDisplay()}
            size="xs"
            activePrograms={affiliate.totalStats.activePrograms}
            pendingPrograms={affiliate.totalStats.pendingPrograms}
            inactivePrograms={affiliate.totalStats.inactivePrograms}
          />
          {isSingleProgramContext &&
            displayedProgram?.status === AffiliateProgramStatus.REMOVED && (
              <Badge mt="1" colorPalette="gray" size="xs">
                {t('contactDrawer.affiliation.status.removed')}
              </Badge>
            )}
        </Table.Cell>
        <Table.Cell>{renderNetworksCell()}</Table.Cell>

        <Table.Cell textAlign="center" {...sortedColumnCellProps('orders')}>
          <FormatNumber value={affiliate.totalStats.ordersCount} />
        </Table.Cell>
        <Table.Cell textAlign="center" {...sortedColumnCellProps('visits')}>
          <FormatNumber value={affiliate.totalStats.visits} />
        </Table.Cell>
        <Table.Cell textAlign="right" {...sortedColumnCellProps('revenue')}>
          <Money
            fontSize="sm"
            value={affiliate.totalStats.revenue.amount}
            currency={affiliate.totalStats.revenue.currencyCode}
          />
        </Table.Cell>
        <Table.Cell textAlign="right" {...sortedColumnCellProps('conversion')}>
          {!affiliate.totalStats.conversionRate ? (
            <Text color="fg.muted">{t('dashboard.stats.notAvailable')}</Text>
          ) : (
            <FormatNumber
              value={affiliate.totalStats.conversionRate / 100}
              style="percent"
              minimumFractionDigits={0}
              maximumFractionDigits={2}
            />
          )}
        </Table.Cell>
        <Table.Cell textAlign="right" {...sortedColumnCellProps('averageOrderValue')}>
          <Money
            fontSize="sm"
            value={affiliate.totalStats.averageOrderValue.amount}
            currency={affiliate.totalStats.averageOrderValue.currencyCode}
          />
        </Table.Cell>
        <Table.Cell textAlign="right" {...sortedColumnCellProps('payableCommission')}>
          <Money
            fontSize="sm"
            value={affiliate.totalStats.payableCommission.amount}
            currency={affiliate.totalStats.payableCommission.currencyCode}
          />
        </Table.Cell>
        <Table.Cell textAlign="center">
          <Badge
            size="sm"
            colorPalette={PAYOUT_STATUS_COLOR[affiliate.totalStats.payoutStatus]}
            variant="subtle"
          >
            {t(`affiliates.table.payoutStatus.${affiliate.totalStats.payoutStatus}`)}
          </Badge>
        </Table.Cell>
        <Table.Cell {...sortedColumnCellProps('lastOrderAt')}>
          <DateCell dateString={affiliate.totalStats.lastOrderAt} />
        </Table.Cell>
        <Table.Cell>{renderOriginCell()}</Table.Cell>
        <Table.Cell>
          {copyValue ? (
            <HStack gap={1} justify="flex-start" minW={0}>
              <Text fontSize="xs" fontFamily="mono" truncate maxW="120px">
                {displayedProgram?.referralCode || t('affiliates.table.columns.referral')}
              </Text>
              <Tooltip
                content={t('affiliates.table.actions.copyReferral')}
                portalled
                positioning={{ placement: 'top' }}
              >
                <IconButton
                  size="xs"
                  rounded="md"
                  variant="ghost"
                  colorPalette="gray"
                  aria-label={t('affiliates.table.actions.copyReferral')}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleCopyReferral();
                  }}
                >
                  <Copy width={16} height={16} />
                </IconButton>
              </Tooltip>
            </HStack>
          ) : !isSingleProgramContext ? (
            <Text fontSize="sm" color="fg.muted">
              {t('affiliates.table.actions.multipleReferralCodes', {
                count: affiliate.programs.length,
              })}
            </Text>
          ) : (
            <Text color="fg.muted">{t('dashboard.stats.notAvailable')}</Text>
          )}
        </Table.Cell>
        <Table.Cell {...sortedColumnCellProps('joinedAt')}>
          <Text fontSize="sm">{formatDate(joinedAt)}</Text>
        </Table.Cell>
        <Table.Cell
          width={ACTIONS_COLUMN_WIDTH}
          textAlign="end"
          {...stickyEndColumnProps(ACTIONS_COLUMN_WIDTH)}
        >
          <HStack gap={1} justify="flex-end">
            <AffiliateProgramActions
              userId={affiliate.id}
              affiliateName={affiliate.name || affiliate.email}
              programs={
                isProgramSpecificView && displayedProgram ? [displayedProgram] : affiliate.programs
              }
              onOpen={(id) =>
                openAffiliateSurface(affiliate.id, { view: 'affiliation', collab: id })
              }
            />
            <Tooltip
              content={t('affiliates.table.actions.sendEmail')}
              portalled
              positioning={{ placement: 'top' }}
            >
              <IconButton
                size="xs"
                rounded="md"
                variant="ghost"
                colorPalette="gray"
                aria-label={t('affiliates.table.actions.sendEmail')}
                onClick={(e) => {
                  e.stopPropagation();
                  // Single-recipient email sends from the contact drawer's
                  // outreach view. The bulk-email modal stays for true
                  // multi-select only.
                  openAffiliateSurface(affiliate.id, { view: 'outreach' });
                }}
              >
                <Mail width={18} height={18} />
              </IconButton>
            </Tooltip>
            <Tooltip
              content={t('affiliates.table.actions.viewProfile')}
              portalled
              positioning={{ placement: 'top' }}
            >
              <IconButton
                size="xs"
                rounded="md"
                variant="ghost"
                colorPalette="gray"
                aria-label={t('affiliates.table.actions.viewProfile')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleView(affiliate.id);
                }}
              >
                <Eye width={20} height={20} />
              </IconButton>
            </Tooltip>
          </HStack>
        </Table.Cell>
      </Table.Row>
    );
  };

  const activeFilterItems = useMemo(() => {
    const items: DashboardTableFilterItem[] = [];

    if (search) {
      items.push({
        key: 'search',
        label: t('affiliates.filters.searchLabel'),
        value: search,
        onClear: () => void setSearch(''),
      });
    }

    if (status !== 'all') {
      items.push({
        key: 'status',
        label: t('affiliates.filters.status.label'),
        value: t(`affiliates.filters.status.${status}`),
        onClear: () => void setStatus('all'),
      });
    }

    if (hasDateFilter) {
      const dateFilterType = dateFilter.dateFilterType ?? 'joinedAt';
      items.push({
        key: 'date',
        label: t('affiliates.filters.date.label'),
        value: `${t(`affiliates.filters.date.types.${dateFilterType}`)}: ${formatDashboardTableDateRange(
          dateFilter,
          t('common.none', 'None'),
          ' - '
        )}`,
        onClear: clearDateFilter,
      });
    }

    if (sourceFilter) {
      const value = sourceFilter
        .split(',')
        .map((source) => t(`affiliates.table.sources.${source}`))
        .join(', ');
      items.push({
        key: 'source',
        label: t('affiliates.table.columns.source'),
        value,
        onClear: () => void setSourceFilter(''),
      });
    }

    if (importBatchId) {
      items.push({
        key: 'importBatch',
        label: t('affiliates.filters.importBatch'),
        value: t('affiliates.table.filterNotice.importBatch'),
        onClear: () => void setImportBatchId(''),
      });
    }

    return items;
  }, [
    hasDateFilter,
    importBatchId,
    search,
    setImportBatchId,
    setSearch,
    setSourceFilter,
    setStatus,
    sourceFilter,
    status,
    t,
    clearDateFilter,
    dateFilter,
  ]);

  // Show error state
  if (error) {
    return (
      <EmptyState
        title={t('affiliates.table.error.title')}
        description={error.message}
        icon={<WarningTriangle width={32} height={32} />}
      />
    );
  }

  return (
    <>
      <VStack gap={6} w="full" align="flex-start">
        <AffiliateImportModal presetProgramId={programId} />
        <AffiliateExportModal
          presetProgramId={programId}
          filters={exportFilters}
          selectedAffiliateUserIds={selectAllMatching ? undefined : selectedAffiliateUserIds}
        />
        <FilterPanel
          w="full"
          actionsSlot={
            <AddAffiliateButton
              programId={programId}
              isExportDisabled={isInitialLoading}
              onOpenExport={handleOpenExport}
            />
          }
        >
          <AffiliatesFilter
            search={search}
            status={status as 'active' | 'inactive' | 'all'}
            dateFilter={dateFilter}
            onSearchChange={handleSearchChange}
            onStatusChange={handleStatusChange}
            onDateFilterChange={setDateFilter}
            onDateFilterClear={clearDateFilter}
          />
        </FilterPanel>

        <DashboardTableActiveFilters
          items={activeFilterItems}
          clearLabel={t('affiliates.filters.clearFilter')}
        />

        <AffiliateSelectAllBanner pageIds={pageIds} matchingTotal={matchingTotal} />

        <AffiliateHiddenDebtNotice hiddenPayable={displayData?.hiddenPayable} />

        {isInitialLoading ? (
          <TableSkeleton
            rows={5}
            columns={[
              { width: SELECTION_COLUMN_WIDTH }, // Checkbox
              { width: AFFILIATE_COLUMN_WIDTH }, // Affiliate (name + avatar + program badge + status)
              { width: NETWORKS_COLUMN_WIDTH }, // Social networks
              { width: '120px' }, // Orders
              { width: '120px' }, // Visits
              { width: '150px' }, // Revenue
              { width: '120px' }, // Conversion
              { width: '150px' }, // Average order value
              { width: '150px' }, // Payable commission
              { width: '130px' }, // Payout status
              { width: '150px' }, // Last order
              { width: '130px' }, // Source
              { width: '150px' }, // Link/code
              { width: '130px' }, // Joined date
              { width: ACTIONS_COLUMN_WIDTH }, // Actions
            ]}
            stickyColumns={2}
            stickyLastColumn
            firstColumnWidth={SELECTION_COLUMN_WIDTH}
            secondColumnWidth={AFFILIATE_COLUMN_WIDTH}
            lastColumnWidth={ACTIONS_COLUMN_WIDTH}
            scrollAreaStyle={AFFILIATES_TABLE_STYLE}
          />
        ) : !affiliates.length ? (
          renderEmptyState()
        ) : (
          <>
            <TableScrollArea
              style={AFFILIATES_TABLE_STYLE}
              opacity={isFetching && !isInitialLoading ? 0.6 : 1}
              transition="opacity 0.15s"
              pointerEvents={isFetching && !isInitialLoading ? 'none' : 'auto'}
            >
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader
                      width={SELECTION_COLUMN_WIDTH}
                      {...stickyColumnProps(SELECTION_COLUMN_WIDTH, '0px', { divider: 'end' })}
                    >
                      <Checkbox
                        size="sm"
                        checked={
                          allPageSelected ? true : somePageSelected ? 'indeterminate' : false
                        }
                        onCheckedChange={() =>
                          selectAllMatching ? clearSelection() : toggleSelectAll(pageIds)
                        }
                      />
                    </Table.ColumnHeader>
                    <Table.ColumnHeader
                      width={AFFILIATE_COLUMN_WIDTH}
                      {...stickyColumnProps(AFFILIATE_COLUMN_WIDTH, SELECTION_COLUMN_WIDTH)}
                    >
                      {t('affiliates.table.columns.affiliate')}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader width={NETWORKS_COLUMN_WIDTH}>
                      {t('affiliates.table.columns.networks')}
                    </Table.ColumnHeader>
                    <SortableColumnHeader
                      column="orders"
                      label={t('affiliates.table.columns.orders')}
                      sort={tableSort}
                      defaultSort={AFFILIATE_DEFAULT_SORT}
                      onSortChange={setTableSort}
                      width="120px"
                      textAlign="center"
                    />
                    <SortableColumnHeader
                      column="visits"
                      label={t('affiliates.table.columns.visits')}
                      sort={tableSort}
                      defaultSort={AFFILIATE_DEFAULT_SORT}
                      onSortChange={setTableSort}
                      width="120px"
                      textAlign="center"
                    />
                    <SortableColumnHeader
                      column="revenue"
                      label={t('affiliates.table.columns.revenue')}
                      sort={tableSort}
                      defaultSort={AFFILIATE_DEFAULT_SORT}
                      onSortChange={setTableSort}
                      width="150px"
                      textAlign="right"
                    />
                    <SortableColumnHeader
                      column="conversion"
                      label={t('affiliates.table.columns.conversion')}
                      sort={tableSort}
                      defaultSort={AFFILIATE_DEFAULT_SORT}
                      onSortChange={setTableSort}
                      width="120px"
                      textAlign="right"
                    />
                    <SortableColumnHeader
                      column="averageOrderValue"
                      label={t('affiliates.table.columns.averageOrderValue')}
                      sort={tableSort}
                      defaultSort={AFFILIATE_DEFAULT_SORT}
                      onSortChange={setTableSort}
                      width="150px"
                      textAlign="right"
                    />
                    <SortableColumnHeader
                      column="payableCommission"
                      label={t('affiliates.table.columns.payable')}
                      sort={tableSort}
                      defaultSort={AFFILIATE_DEFAULT_SORT}
                      onSortChange={setTableSort}
                      width="150px"
                      textAlign="right"
                    />
                    <Table.ColumnHeader width="130px" textAlign="center">
                      {t('affiliates.table.columns.payoutStatus')}
                    </Table.ColumnHeader>
                    <SortableColumnHeader
                      column="lastOrderAt"
                      label={t('affiliates.table.columns.lastOrder')}
                      sort={tableSort}
                      defaultSort={AFFILIATE_DEFAULT_SORT}
                      onSortChange={setTableSort}
                      width="150px"
                    />
                    <Table.ColumnHeader width="130px">
                      {t('affiliates.table.columns.source')}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader width="150px">
                      {t('affiliates.table.columns.referral')}
                    </Table.ColumnHeader>
                    <SortableColumnHeader
                      column="joinedAt"
                      label={t('affiliates.table.columns.joinedAt')}
                      sort={tableSort}
                      defaultSort={AFFILIATE_DEFAULT_SORT}
                      onSortChange={setTableSort}
                      width="130px"
                    />
                    <Table.ColumnHeader
                      width={ACTIONS_COLUMN_WIDTH}
                      textAlign="end"
                      aria-label={t('affiliates.table.columns.actions')}
                      {...stickyEndColumnProps(ACTIONS_COLUMN_WIDTH)}
                    />
                  </Table.Row>
                </Table.Header>
                <Table.Body>{affiliates.map(renderAffiliateRow)}</Table.Body>
              </Table.Root>
            </TableScrollArea>

            {/* Pagination Controls */}
            {paginationMeta && (
              <PaginationControls
                pagination={{
                  total: paginationMeta.total,
                  perPage: paginationMeta.perPage,
                  currentPage: paginationMeta.currentPage,
                  lastPage: paginationMeta.lastPage,
                  from: (paginationMeta.currentPage - 1) * paginationMeta.perPage + 1,
                  to: Math.min(
                    paginationMeta.currentPage * paginationMeta.perPage,
                    paginationMeta.total
                  ),
                }}
                onPageChange={handlePageChange}
                isLoading={isFetching}
              />
            )}
          </>
        )}
      </VStack>

      {/* Bulk email selection bar + modal */}
      <AffiliateBulkActionBar
        affiliates={recipientList}
        onExport={handleOpenExport}
        onSendEmail={() => setIsBulkEmailOpen(true)}
      />
      <BulkAffiliateEmailModal
        isOpen={isBulkEmailOpen}
        onClose={() => setIsBulkEmailOpen(null)}
        programId={programId}
        affiliates={recipientList}
        filters={bulkEmailFilters}
      />
    </>
  );
};

export default AffiliatesTable;
